import { List, Map, Set } from 'immutable';

import { setCategoryErrors, setFilesErrors, setIsSupplyErrors, setProductionPartnersErrors, setWhenMadeErrors, setWhoMadeErrors, setTaxonomyAttributesErrors, setConditionErrors, setItemSpecificsErrors, setProductDetailsErrors } from '../../product/errors';
import { getAttributesForCategory, getOversetAttributes, updateItemSpecificsForCategory, updateProductDetailsForCategory } from '../../product/taxonomy';
import { getProductionPartnersType } from '../../product/checkAttribute';
import { getEtsyTaxonomyAttribute } from '../taxonomy';
import { shapeEbayDetailsForApp } from '../../product/shapeAttributes';
import { getPropertyType } from '../../taxonomy/properties';
import { isTruthy } from '../../bool';
import { getSize } from '../../iterable/getSize';
import { get } from '../../iterable/get';

import { ERROR, PRODUCTION_PARTNERS_TYPE, TAXONOMY_ATTRIBUTE, VALUE } from '../../../constants/product';
import { EBAY, ETSY, FAIRE, SHOPIFY } from '../../../constants/channels';
import { OPERATIONS } from '../../../constants/bulkEdit';
import { ATTRIBUTE } from '../../../constants/attributes';
import { ERRORS } from '../../../constants/validations';
import { MODALS } from '../../../constants/modal';
import { EMPTY } from '../../../constants';
import { FORM } from '../../../constants/forms';

export function updateEbayDetails({ operation, product: source, shopData, taxonomyData }) {
  const operationType = operation.get('type');
  let product = source;

  switch (operationType) {
    case OPERATIONS[EBAY].CONDITION_ID: {
      const condition = operation.get('value');
      const category = product.getIn([FORM.DETAILS, ATTRIBUTE.ITEM_CATEGORY, VALUE]);
      const conditions = get(['CATEGORIES', category, 'conditions'])(taxonomyData) || {};

      if (
        getSize(conditions.ids) > 1 &&
        conditions.byId[condition] &&
        product.getIn([FORM.DETAILS, ATTRIBUTE.CONDITION, VALUE]) !== condition
      ) {
        product = setConditionErrors({
          product: product.setIn([FORM.DETAILS, ATTRIBUTE.CONDITION, VALUE], condition),
          taxonomyData,
        });

        if (product.getIn([FORM.DETAILS, ATTRIBUTE.CONDITION, ERROR]) !== ERRORS.DETAILS.CONDITION_ID) {
          return { product };
        }
      }

      return { product: source };
    }

    case OPERATIONS[EBAY].ITEM_CATEGORY: {
      const category = operation.get('value');
      product = product.setIn([FORM.DETAILS, ATTRIBUTE.ITEM_CATEGORY, VALUE], category);
      product = setCategoryErrors({ product, taxonomyData });
      product = setConditionErrors({ product, taxonomyData });
      product = setItemSpecificsErrors({
        product: product.setIn([FORM.DETAILS, ATTRIBUTE.ITEM_SPECIFICS],
          updateItemSpecificsForCategory({
            category,
            channel: EBAY,
            itemSpecifics: product.getIn([FORM.DETAILS, ATTRIBUTE.ITEM_SPECIFICS], EMPTY.MAP),
            taxonomyData,
          })
        ),
        shopData,
        taxonomyData,
      });

      product = setProductDetailsErrors({
        product: product.setIn([FORM.DETAILS, ATTRIBUTE.PRODUCT_DETAILS],
          updateProductDetailsForCategory({
            category,
            channel: EBAY,
            productDetails: product.getIn([FORM.DETAILS, ATTRIBUTE.PRODUCT_DETAILS], EMPTY.MAP),
            taxonomyData,
          })
        ),
        taxonomyData,
      });

      const conditions = get(['CATEGORIES', category, 'conditions'])(taxonomyData) || {};

      if (
        getSize(conditions.ids) === 1 &&
        product.getIn([FORM.DETAILS, ATTRIBUTE.CONDITION, VALUE]) !== conditions.ids[0]
      ) {
        product = product.setIn([FORM.DETAILS, ATTRIBUTE.CONDITION, VALUE], conditions.ids[0]);
        product = setConditionErrors({ product, taxonomyData });
      }

      return { product };
    }

    case OPERATIONS[EBAY].ITEM_SPECIFICS.SET: {
      const category = product.getIn([FORM.DETAILS, ATTRIBUTE.ITEM_CATEGORY, VALUE]);
      const itemSpecifics = updateItemSpecificsForCategory({
        category,
        channel: EBAY,
        itemSpecifics: Map({ [VALUE]: shapeEbayDetailsForApp(operation.get('value')) }),
        taxonomyData,
      });

      const updatedProduct = setItemSpecificsErrors({
        product: product.setIn([FORM.DETAILS, ATTRIBUTE.ITEM_SPECIFICS], itemSpecifics),
        shopData,
        taxonomyData,
      });

      if (getSize(updatedProduct.getIn([FORM.DETAILS, ATTRIBUTE.ITEM_SPECIFICS, ERROR])) === 0) {
        return { product: updatedProduct };
      }

      return { product };
    }

    case OPERATIONS[EBAY].ITEM_SPECIFICS.UPDATE: {
      let updatedProduct = product;
      const category = updatedProduct.getIn([FORM.DETAILS, ATTRIBUTE.ITEM_CATEGORY, VALUE]);

      for (const [name, operationValue] of operation.get('value').entries()) {
        const property = get(['CATEGORIES', category, 'properties', name])(taxonomyData);
        const productValue = updatedProduct.getIn([FORM.DETAILS, ATTRIBUTE.ITEM_SPECIFICS, VALUE]);
        const value = operationValue.get('value');

        if (!property || (getSize(value) === 0 && !productValue.has(name))) continue;

        const result = getSize(value) === 0
          ? productValue.delete(name)
          : productValue.has(name)
            ? productValue.setIn([name, 'value'], Set(value))
            : productValue.set(name,
              Map({
                name,
                required: !!property.reqired,
                type: getPropertyType(property),
                value: Set(value),
              })
            );

        const updated = setItemSpecificsErrors({
          product: updatedProduct.setIn([FORM.DETAILS, ATTRIBUTE.ITEM_SPECIFICS, VALUE], result),
          shopData,
          taxonomyData,
        });

        if (!updated.hasIn([FORM.DETAILS, ATTRIBUTE.ITEM_SPECIFICS, ERROR, name])) {
          updatedProduct = updated;
        }
      }

      return { product: updatedProduct };
    }

    case OPERATIONS[EBAY].PRODUCT_DETAILS.SET: {
      const category = product.getIn([FORM.DETAILS, ATTRIBUTE.ITEM_CATEGORY, VALUE]);
      const productDetails = updateProductDetailsForCategory({
        category,
        channel: EBAY,
        productDetails: Map({ [VALUE]: shapeEbayDetailsForApp(operation.get('value')) }),
        taxonomyData,
      });

      const updatedProduct = setProductDetailsErrors({
        product: product.setIn([FORM.DETAILS, ATTRIBUTE.PRODUCT_DETAILS], productDetails),
        taxonomyData,
      });

      if (getSize(updatedProduct.getIn([FORM.DETAILS, ATTRIBUTE.PRODUCT_DETAILS, ERROR])) === 0) {
        return { product: updatedProduct };
      }

      return { product };
    }

    case OPERATIONS[EBAY].PRODUCT_DETAILS.UPDATE: {
      let updatedProduct = product;
      const category = updatedProduct.getIn([FORM.DETAILS, ATTRIBUTE.ITEM_CATEGORY, VALUE]);

      for (const [name, operationValue] of operation.get('value').entries()) {
        const property = get(['CATEGORIES', category, 'productDetails', name])(taxonomyData);
        const productValue = updatedProduct.getIn([FORM.DETAILS, ATTRIBUTE.PRODUCT_DETAILS, VALUE]);
        const value = operationValue.get('value');

        if (!property || (getSize(value) === 0 && !productValue.has(name))) continue;

        const result = getSize(value) === 0
          ? productValue.delete(name)
          : productValue.has(name)
            ? productValue.setIn([name, 'value'], Set(value))
            : productValue.set(name,
              Map({
                name,
                required: !!property.reqired,
                type: getPropertyType(property),
                value: Set(value),
              })
            );

        const updated = setProductDetailsErrors({
          product: updatedProduct.setIn([FORM.DETAILS, ATTRIBUTE.PRODUCT_DETAILS, VALUE], result),
          taxonomyData,
        });

        if (!updated.hasIn([FORM.DETAILS, ATTRIBUTE.PRODUCT_DETAILS, ERROR, name])) {
          updatedProduct = updated;
        }
      }

      return { product: updatedProduct };
    }

    case OPERATIONS[EBAY].STORE_CATEGORY: {
      return { product: product.setIn([FORM.DETAILS, ATTRIBUTE.STORE_CATEGORY, VALUE], operation.get('value')) };
    }

    default: {
      return { product };
    }
  }
}

export function updateFaireDetails({ operation, product: source }) {
  const operationType = operation.get('type');
  let product = source;

  switch (operationType) {
    case OPERATIONS[FAIRE].CATEGORY: {
      const category = operation.get('value');
      product = product.setIn([FORM.DETAILS, ATTRIBUTE.CATEGORY, VALUE], category);
      return { product };
    }

    default: {
      return { product };
    }
  }
}

export function updateEtsyDetails({ operation, product: source }) {
  const operationType = operation.get('type');
  let product = source;

  switch (operationType) {
    case OPERATIONS[ETSY].CATEGORY: {
      const category = operation.get('value');
      product = setCategoryErrors({ product: product.setIn([FORM.DETAILS, ATTRIBUTE.CATEGORY, VALUE], category) });

      const { taxonomyAttributes } = getAttributesForCategory({
        category,
        taxonomyAttributes: product.getIn([FORM.DETAILS, ATTRIBUTE.TAXONOMY_ATTRIBUTES], EMPTY.MAP),
      });

      product = setTaxonomyAttributesErrors({
        product: product.setIn([FORM.DETAILS, ATTRIBUTE.TAXONOMY_ATTRIBUTES], taxonomyAttributes),
      });

      return { confirmation: MODALS.CONFIRMATIONS.CHANGE_CATEGORY, product };
    }

    case OPERATIONS[ETSY].TAXONOMY_ATTRIBUTES: {
      if (product.getIn([FORM.DETAILS, ATTRIBUTE.CATEGORY, ERROR])) return { product };

      const category = product.getIn([FORM.DETAILS, ATTRIBUTE.CATEGORY, VALUE]);

      operation.get('value').forEach(function forEachOperationValue(operationValue, key) {
        const property = parseInt(key);

        if (getOversetAttributes(product.get(FORM.VARIATIONS)).has(property)) return;

        const channel = product.get('channel');
        const scaleId = operationValue.get('scaleId');
        const values = operationValue.get('values');
        const valueIds = operationValue.get('valueIds');
        const attribute = getEtsyTaxonomyAttribute({ category, channel, property, scale: scaleId });
        let value = Map({ scaleId, values, valueIds });

        function filterValueIds(valueId) {
          return attribute.hasIn(['options', 'byId', valueId]);
        }

        function getOptionLabel(valueId) {
          return attribute.getIn(['options', 'byId', valueId, 'name']);
        }

        if (attribute.get('type') === TAXONOMY_ATTRIBUTE.SCALED) {
          if (scaleId === null || !attribute.hasIn(['scales', 'byId', scaleId])) return;
        } else {
          if (getSize(valueIds) > 0) {
            value = value.set('valueIds', valueIds.filter(filterValueIds));

            if (getSize(value.get('valueIds')) > 0) {
              if (getSize(value.get('valueIds')) !== getSize(valueIds)) {
                value = value.set('values', valueIds.map(getOptionLabel));
              }
            } else {
              return;
            }
          } else if (attribute.get('required')) {
            return;
          }
        }

        const defaultIds = attribute.getIn(['selected', 'list'], EMPTY.LIST);
        const mergedValueIds = List([...new Set(defaultIds.concat(valueIds).toArray())]);
        const mergedValues = mergedValueIds.map(getOptionLabel);
        const taxonomyAttributes = product
          .getIn([FORM.DETAILS, ATTRIBUTE.TAXONOMY_ATTRIBUTES])
          .set(property, Map({ scaleId, values: mergedValues, valueIds: mergedValueIds }));

        const updatedProduct = setTaxonomyAttributesErrors({
          product: product.setIn([FORM.DETAILS, ATTRIBUTE.TAXONOMY_ATTRIBUTES], taxonomyAttributes),
        });

        if (!updatedProduct.getIn([FORM.DETAILS, ATTRIBUTE.TAXONOMY_ATTRIBUTES, ERROR, property])) {
          product = updatedProduct;
        }
      });

      return { product };
    }

    case OPERATIONS[ETSY].SECTION: {
      const value = operation.get('value');
      product = product.setIn([FORM.DETAILS, ATTRIBUTE.SECTION, VALUE], value === 'none' ? null : value);
      return { product };
    }

    case OPERATIONS[ETSY].IS_SUPPLY: {
      const value = operation.get('value');
      product = product.setIn([FORM.DETAILS, ATTRIBUTE.IS_SUPPLY, VALUE], value);
      product = setIsSupplyErrors({ product });
      product = setProductionPartnersErrors({ product });
      return { product };
    }

    case OPERATIONS[ETSY].WHEN_MADE: {
      const value = operation.get('value');
      product = product.setIn([FORM.DETAILS, ATTRIBUTE.WHEN_MADE, VALUE], value);
      product = setWhenMadeErrors({ product });
      product = setProductionPartnersErrors({ product });
      product = setFilesErrors({ product });
      return { product };
    }

    case OPERATIONS[ETSY].WHO_MADE: {
      const value = operation.get('value');
      product = product.setIn([FORM.DETAILS, ATTRIBUTE.WHO_MADE, VALUE], value);
      product = setWhoMadeErrors({ product });
      product = setProductionPartnersErrors({ product });
      return { product };
    }

    case OPERATIONS[ETSY].PRODUCTION_PARTNERS: {
      const value = operation.get('value');
      const isSupply = product.getIn([FORM.DETAILS, ATTRIBUTE.IS_SUPPLY, VALUE]);
      const whenMade = product.getIn([FORM.DETAILS, ATTRIBUTE.WHEN_MADE, VALUE]);
      const whoMade = product.getIn([FORM.DETAILS, ATTRIBUTE.WHO_MADE, VALUE]);
      const type = getProductionPartnersType({ isSupply, whenMade, whoMade });

      if (type === PRODUCTION_PARTNERS_TYPE.UNAVAILABLE) {
        product = setProductionPartnersErrors({
          product: product.setIn([FORM.DETAILS, ATTRIBUTE.PRODUCTION_PARTNERS, VALUE], EMPTY.LIST),
        });

        return { product };
      }

      const updatedProduct = setProductionPartnersErrors({
        product: product.setIn([FORM.DETAILS, ATTRIBUTE.PRODUCTION_PARTNERS, VALUE], value),
      });

      if (!updatedProduct.getIn([FORM.DETAILS, ATTRIBUTE.PRODUCTION_PARTNERS, ERROR])) {
        product = updatedProduct;
      }

      return { product };
    }

    default: {
      return { product };
    }
  }
}

export function updateShopifyDetails({ operation, product }) {
  const operationType = operation.get('type');

  switch (operationType) {
    case OPERATIONS[SHOPIFY].CATEGORY: {
      const category = operation.get('value');
      return { product: product.setIn([FORM.DETAILS, ATTRIBUTE.CATEGORY, VALUE], category) };
    }

    case OPERATIONS[SHOPIFY].COLLECTIONS: {
      const collections = List(operation.get('value')).filter(isTruthy);
      return { product: product.setIn([FORM.DETAILS, ATTRIBUTE.COLLECTIONS, VALUE], collections) };
    }

    case OPERATIONS[SHOPIFY].PRODUCT_TYPE: {
      const value = operation.get('value');
      return { product: product.setIn([FORM.DETAILS, ATTRIBUTE.PRODUCT_TYPE, VALUE], value === '' ? null : value) };
    }

    case OPERATIONS[SHOPIFY].VENDOR: {
      const value = operation.get('value');
      return { product: product.setIn([FORM.DETAILS, ATTRIBUTE.VENDOR, VALUE], value === '' ? null : value) };
    }

    default: {
      return { product };
    }
  }
}

export default function details({ actions, confirmation, operation, product, shopData, taxonomyData }) {
  switch (product.get('channel')) {
    case EBAY: {
      return { actions, confirmation, ...updateEbayDetails({ operation, product, shopData, taxonomyData }) };
    }

    case ETSY: {
      return { actions, confirmation, ...updateEtsyDetails({ operation, product }) };
    }

    case FAIRE: {
      return { actions, confirmation, ...updateFaireDetails({ operation, product }) };
    }

    case SHOPIFY: {
      return { actions, confirmation, ...updateShopifyDetails({ operation, product }) };
    }

    default: {
      return { confirmation, actions, product };
    }
  }
}
